import { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";

const ShareRoom = ({ roomCode, isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const joinUrl = `${window.location.origin}/join?room=${encodeURIComponent(roomCode)}`;

  // Dynamic sizing so long room names stay INSIDE the box (max 2 lines, downscale)
  const len = (roomCode || "").length;
  const codeSizeClass =
    len <= 8
      ? "text-5xl sm:text-6xl tracking-widest"
      : len <= 16
      ? "text-4xl sm:text-5xl tracking-wide"
      : len <= 32
      ? "text-3xl sm:text-4xl tracking-normal"
      : len <= 60
      ? "text-xl sm:text-2xl leading-tight"
      : "text-base sm:text-lg leading-snug";

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = joinUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
        onClick={onClose}
        data-testid="share-backdrop"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="backdrop-blur-2xl bg-black/80 border border-white/20 rounded-3xl p-6 sm:p-8 shadow-2xl w-full max-w-md mx-auto pointer-events-auto animate-in fade-in zoom-in duration-300">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 
              className="text-3xl font-primary text-[#00F0FF]"
              style={{ fontFamily: 'Anton, sans-serif' }}
            >
              INVITE PLAYERS
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-[#FF0055] transition-colors"
              data-testid="close-share-button"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Room Code Display — clamps to at most 2 lines and downscales for long names */}
          <div className="text-center mb-6 w-full">
            <p className="text-[#A1A1AA] text-sm mb-2 uppercase tracking-wider">Room Code</p>
            <p
              className={`font-primary text-[#FFEA00] drop-shadow-[0_0_15px_rgba(255,234,0,0.5)] px-2 break-words line-clamp-2 max-w-full ${codeSizeClass}`}
              style={{
                fontFamily: "Anton, sans-serif",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
              title={roomCode}
              data-testid="share-room-code"
            >
              {roomCode}
            </p>
          </div>

          {/* QR Code */}
          <div className="bg-white p-4 rounded-2xl mx-auto w-fit mb-6" data-testid="qr-code-container">
            <QRCodeSVG 
              value={joinUrl}
              size={180}
              level="M"
              fgColor="#050508"
              bgColor="#FFFFFF"
            />
          </div>

          <p className="text-center text-[#A1A1AA] text-sm mb-4">
            Scan QR code or share the link below
          </p>

          {/* Link & Copy */}
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={joinUrl}
              readOnly
              className="flex-1 bg-white/5 border border-white/10 text-white rounded-xl px-4 py-3 text-sm font-secondary focus:outline-none truncate"
              data-testid="share-link-input"
            />
            <button
              onClick={handleCopy}
              className="px-4 py-3 bg-[#00F0FF] text-black font-primary rounded-xl shadow-[0_0_15px_rgba(0,240,255,0.4)] hover:scale-105 active:scale-95 transition-all uppercase text-sm whitespace-nowrap"
              style={{ fontFamily: 'Anton, sans-serif' }}
              data-testid="copy-link-button"
            >
              {copied ? "COPIED!" : "COPY"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShareRoom;
